import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar, Card, Col, Row, Select, Statistic, Table, Tag, Typography, message, Space,
} from 'antd';
import { CrownOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAdminPromotersApi } from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';

const { Title, Text } = Typography;

const SORT_OPTIONS = [
  { value: 'referrer_count',   label: 'Most Referrals' },
  { value: 'subscribed_count', label: 'Most Converted' },
];

export default function AdminPromoters() {
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState(null);
  const [data, setData]         = useState([]);
  const [meta, setMeta]         = useState({ total: 0, current_page: 1, per_page: 20 });
  const [sortBy, setSortBy]     = useState('referrer_count');

  const fetchPromoters = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getAdminPromotersApi({ sort_by: sortBy, page, per_page: meta.per_page });
      const body = res.data;
      setSummary(body.summary || null);
      setData(body.data || []);
      setMeta(body.meta || meta);
    } catch {
      message.error('Failed to load promoters');
    } finally {
      setLoading(false);
    }
  }, [sortBy, meta.per_page]);

  useEffect(() => { fetchPromoters(1); }, [sortBy]);

  const columns = [
    {
      title: 'Promoter',
      key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar
            src={r.photo ? USER_IMAGE(r.photo) : null}
            icon={<UserOutlined />}
            size={36}
            style={{ background: '#e8f5e9', color: '#1a6b2a', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.mobile}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Ref Code',
      dataIndex: 'share_ref_code',
      width: 110,
      render: (v) => v ? <Tag color="green">{v}</Tag> : '-',
    },
    {
      title: 'Prime Expires',
      dataIndex: 'user_expire_on',
      width: 120,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
    {
      title: 'Total Referred',
      dataIndex: 'referrer_count',
      width: 120,
      render: (v) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>{v}</Tag>
      ),
    },
    {
      title: 'Subscribed',
      dataIndex: 'subscribed_count',
      width: 110,
      render: (v) => (
        <Tag color="gold" icon={<CrownOutlined />} style={{ fontWeight: 600 }}>{v}</Tag>
      ),
    },
    {
      title: 'Not Subscribed',
      dataIndex: 'not_subscribed_count',
      width: 130,
      render: (v) => (
        <Tag color="default" style={{ fontWeight: 600 }}>{v}</Tag>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Promoters</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Users who referred others</Text>
        </div>
        <Select
          value={sortBy}
          onChange={setSortBy}
          options={SORT_OPTIONS}
          style={{ width: 170 }}
        />
      </div>

      {summary && (
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Promoters', value: summary.total_promoters, color: '#1a6b2a', bg: '#f6ffed' },
            { label: 'Total Referred',  value: summary.total_referred,  color: '#1677ff', bg: '#e6f4ff' },
            { label: 'Total Subscribed',value: summary.total_subscribed,color: '#d4a017', bg: '#fffbe6' },
          ].map((item) => (
            <Col xs={24} sm={8} key={item.label}>
              <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
                <Statistic
                  title={item.label}
                  value={item.value}
                  valueStyle={{ color: item.color, fontSize: 22, fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card style={{ borderRadius: 10, padding: 0 }} styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 800 }}
          onRow={(r) => ({
            onClick: () => navigate(`/admin/users/${r.id}`, { state: { user: r } }),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (t) => `${t} promoters`,
            onChange: (page) => fetchPromoters(page),
          }}
          size="small"
        />
      </Card>
    </div>
  );
}
